/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactElement, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RiAlertFill } from 'react-icons/ri';
import { useIdentityServiceCreateGroupMutation } from '../../../api/enhanced/enhancedIdentity';
import Modal from '../../../components/modal/modal';
import FormErrorLabel from '../../../components/form/formErrorLabel';
import FormTextInput from '../../../components/form/formTextInput';
import FormSubmitButton from '../../../components/form/formSubmitButton';

interface FormValues {
  description: string;
}

interface CreateGroupModalProps {
  closeHandler: () => void;
  isOpen: boolean;
}

function CreateGroupModal({
  closeHandler,
  isOpen
}: CreateGroupModalProps): ReactElement {
  const methods = useForm<FormValues>();
  const { t } = useTranslation();
  const [createGroup, { isLoading, isError, isSuccess, reset }] =
    useIdentityServiceCreateGroupMutation();

  const onSubmit = (data: FormValues): void => {
    createGroup({
      v1Alpha1CreateGroupRequest: {
        description: data.description.trim()
      }
    });
  };

  const close = (): void => {
    closeHandler();
    methods.reset();
    reset();
  };

  useEffect(() => {
    if (isSuccess) {
      reset();
      methods.reset();
      closeHandler();
    }
  }, [isSuccess, methods, reset, closeHandler]);

  return (
    <FormProvider {...methods}>
      <Modal
        isOpen={isOpen}
        closeHandler={() => {
          close();
        }}
        title={t('modals.createGroup.title')}
      >
        <div className="w-full flex flex-col">
          {isError ? (
            <FormErrorLabel
              className="mb-3"
              icon={<RiAlertFill size={20} />}
              label={t('modals.error')}
            />
          ) : null}

          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <FormTextInput
              id="description"
              label={t('modals.createGroup.description.label')}
              errorMsg={t('modals.createGroup.description.error') as string}
              placeholder={t('modals.createGroup.description.label') as string}
              disabled={isLoading}
              required
            />

            <FormSubmitButton label={t('modals.save')} isLoading={isLoading} />
          </form>
        </div>
      </Modal>
    </FormProvider>
  );
}

export default CreateGroupModal;
